import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { Btn, Card, Field, Input, Loading, Pill, Select } from "@/components/Ui";

const RULES = [
  ["exact", "Exact match — different value is a deviation"],
  ["choice", "Fixed option list — different option is a deviation"],
  ["ordered", "Ranked scale — a higher rank satisfies a lower one"],
  ["numeric_min", "Numeric, higher is acceptable (IP, lumens, kA)"],
  ["numeric_max", "Numeric, lower is acceptable (power draw)"],
  ["numeric_exact", "Numeric must match"],
  ["range_contains", "Range — the offered range must cover the requirement"],
  ["certificate", "Certificate reference — a different one is a deviation"],
  ["informational", "Shown for context only, never affects the verdict"],
];
const TYPES = [["text", "Text"], ["number", "Number"], ["choice", "Option list"], ["range", "Range"]];

const blankField = { key: "", label: "", rule: "exact", type: "text", unit: "", options: [], mandatory: false };

export default function AdminSpecCategories() {
  const schema = useSpecSchema();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/spec-definitions");
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const edit = (row) => {
    setOpen(row.category === open ? null : row.category);
    setDraft((row.fields || []).map((f) => ({ ...f, options: f.options || [] })));
  };

  const save = async (row, extra = null) => {
    setBusy(true);
    try {
      // Only send fields when the editor is genuinely open for THIS row, otherwise a
      // publish/unpublish would overwrite the category's fields with an empty list.
      const body = extra ?? { fields: draft };
      await api.put(`/admin/spec-definitions/${encodeURIComponent(row.category)}`, body);
      toast.success(`${row.category} updated`);
      await load();
      schema.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const create = async () => {
    if (!newCategory.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/spec-definitions", { category: newCategory.trim(), fields: [] });
      toast.success(`${newCategory.trim()} created as a draft`);
      setNewCategory("");
      await load();
      schema.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (row) => {
    setBusy(true);
    try {
      await api.delete(`/admin/spec-definitions/${encodeURIComponent(row.category)}`);
      toast.success(`${row.category} removed`);
      await load();
      schema.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setField = (i, patch) => setDraft((d) => d.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  if (!rows) return <Loading label="Loading category specifications…" />;

  return (
    <div className="space-y-4" data-testid="admin-spec-categories">
      <Card>
        <h2 className="font-display text-base font-bold tracking-tight text-[#102A43]">
          Categories &amp; specification fields
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-[#667788]">
          Each category owns its own technical fields and the comparison rule for each one. These
          drive the product forms, the search filters, the Excel template and the equivalence
          comparison — so adding a category here is all that is needed to open the platform to a new
          product type. Shared fields (Ex protection, gas group, IP, temperature, material,
          certificates) are added to every category automatically. A <strong>draft</strong> category
          is hidden from buyers until you publish it.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-2">
          <Field label="New category name" className="min-w-[260px]">
            <Input data-testid="new-category-input" value={newCategory}
              placeholder="e.g. Contactors & Relays"
              onChange={(e) => setNewCategory(e.target.value)} />
          </Field>
          <Btn data-testid="create-category-btn" loading={busy} onClick={create} className="px-4 py-2.5 text-xs">
            <Plus className="h-3.5 w-3.5" /> Add category
          </Btn>
        </div>
      </Card>

      {rows.map((row) => (
        <Card key={row.category} data-testid={`spec-cat-${row.category}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-display text-base font-bold text-[#102A43]">{row.category}</p>
                <Pill tone={row.status === "published" ? "ok" : "warn"}
                  testId={`spec-cat-status-${row.category}`}>
                  {row.status}
                </Pill>
                <span className="text-xs text-[#8598ac]">
                  {row.resolved.length} fields · {row.product_count} product(s)
                </span>
              </div>
              <p className="mt-1 text-xs text-[#8598ac]">
                Own fields: {(row.fields || []).map((f) => f.label).join(", ") || "none yet"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Btn variant="ghost" data-testid={`edit-cat-${row.category}`} onClick={() => edit(row)}
                className="px-3.5 py-2 text-xs">
                {open === row.category ? "Close" : "Edit fields"}
              </Btn>
              <Btn variant="ghost" loading={busy} className="px-3.5 py-2 text-xs"
                data-testid={`publish-cat-${row.category}`}
                onClick={() => save(row, { status: row.status === "published" ? "draft" : "published" })}>
                {row.status === "published" ? "Unpublish" : "Publish"}
              </Btn>
              {row.product_count === 0 && (
                <Btn variant="ghost" loading={busy} className="px-3 py-2 text-xs"
                  data-testid={`delete-cat-${row.category}`} onClick={() => remove(row)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Btn>
              )}
            </div>
          </div>

          {open === row.category && (
            <div className="mt-5 space-y-3 border-t border-[#DFE6ED] pt-4">
              {draft.map((f, i) => (
                <div key={i} className="grid gap-3 rounded-[9px] bg-[#F5F7FA] p-3 sm:grid-cols-2 lg:grid-cols-5">
                  <Field label="Field key">
                    <Input data-testid={`field-key-${i}`} value={f.key}
                      onChange={(e) => setField(i, { key: e.target.value })} />
                  </Field>
                  <Field label="Label buyers see">
                    <Input data-testid={`field-label-${i}`} value={f.label}
                      onChange={(e) => setField(i, { label: e.target.value })} />
                  </Field>
                  <Field label="Data type">
                    <Select data-testid={`field-type-${i}`} value={f.type} options={TYPES}
                      onChange={(e) => setField(i, { type: e.target.value })} />
                  </Field>
                  <Field label="Comparison rule" className="lg:col-span-2">
                    <Select data-testid={`field-rule-${i}`} value={f.rule} options={RULES}
                      onChange={(e) => setField(i, { rule: e.target.value })} />
                  </Field>
                  <Field label="Unit">
                    <Input data-testid={`field-unit-${i}`} value={f.unit || ""}
                      placeholder="mm, °C, A, lm" onChange={(e) => setField(i, { unit: e.target.value })} />
                  </Field>
                  <Field label="Options (comma separated)" className="lg:col-span-3"
                    hint="Used for option lists, and to set the order for a ranked scale (lowest first)">
                    <Input data-testid={`field-options-${i}`} value={(f.options || []).join(", ")}
                      onChange={(e) => setField(i, {
                        options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                      })} />
                  </Field>
                  <div className="flex items-end justify-between gap-2">
                    <label className="flex items-center gap-2 text-sm text-[#334e68]">
                      <input type="checkbox" className="h-4 w-4 accent-[#147D78]"
                        data-testid={`field-mandatory-${i}`} checked={!!f.mandatory}
                        onChange={(e) => setField(i, { mandatory: e.target.checked })} />
                      Mandatory
                    </label>
                    <Btn variant="ghost" className="px-2.5 py-2 text-xs"
                      data-testid={`remove-field-${i}`}
                      onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Btn>
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Btn variant="ghost" data-testid="add-field-btn" className="px-3.5 py-2 text-xs"
                  onClick={() => setDraft((d) => [...d, { ...blankField }])}>
                  <Plus className="h-3.5 w-3.5" /> Add field
                </Btn>
                <Btn data-testid={`save-cat-${row.category}`} loading={busy}
                  onClick={() => save(row, { fields: draft, allow_empty_fields: true })}
                  className="px-4 py-2.5 text-xs">
                  <Save className="h-3.5 w-3.5" /> Save fields
                </Btn>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
